import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  MapPin,
  Phone,
  Mail,
  Globe,
  Clock,
  ArrowLeft,
  Star,
  BadgeCheck,
  Crown,
  Pencil,
  UserCheck,
  QrCode,
  StarOff,
  Scissors,
  CalendarClock,
  Sparkles,
  Users,
  ExternalLink,
  Trash2,
  Camera,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { API } from "@/App";
import { resolveAssetUrl } from "@/lib/assetUrl";
import FavoriteButton from "@/components/FavoriteButton";
import ShareButton from "@/components/ShareButton";
import ReviewsSection from "@/components/ReviewsSection";
import BusinessExternalReviewsSection from "@/components/BusinessExternalReviewsSection";
import { trackEvent } from "@/lib/analytics";

// ── Detection helper — exported so BusinessProfilePage can route here. ──
// A business qualifies as a barber shop when either its category is
// explicitly "Barber" (future dedicated category) OR it's in
// Beauty & Wellness with barber-related keywords in name, custom
// category or description. Kept case-insensitive.
const BARBER_KEYWORDS = [
  "barber",
  "barbershop",
  "cutz",
  "cutz ",
  "cuts &",
  "fades",
  "clippers",
  "grooming",
];
export function isBarberBusiness(business) {
  if (!business) return false;
  const hay = [
    business.category,
    business.custom_category,
    business.name,
    business.description,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return BARBER_KEYWORDS.some((kw) => hay.includes(kw));
}

// Fallback price sheet when a shop hasn't filled theirs in yet.
const DEFAULT_PRICE_SHEET = [
  { service: "Signature Cut", price: "$35" },
  { service: "Beard Line-Up", price: "$20" },
  { service: "Cut + Beard", price: "$50" },
  { service: "Kids Cut (10 & under)", price: "$25" },
];

// Fallback dark barbershop backdrop when no hero portrait is set.
const FALLBACK_BARBER_HERO =
  "https://images.unsplash.com/photo-1503951914875-452162b0f3f1?w=1600&q=80";

// ── Small BC score display ──────────────────────────────────────────────
function BCScoreDisplay({ score, count }) {
  const full = Math.floor(score);
  const half = score % 1 >= 0.5;
  return (
    <div className="flex items-center gap-2">
      <div className="flex">
        {Array.from({ length: 5 }, (_, i) => (
          <Star
            key={`s-${i}`}
            className={`h-5 w-5 ${
              i < full
                ? "fill-tbc-gold text-tbc-gold"
                : i === full && half
                ? "fill-tbc-gold/50 text-tbc-gold"
                : "text-gray-300"
            }`}
          />
        ))}
      </div>
      <span className="text-lg font-heading text-tbc-navy">
        {score > 0 ? score : "—"}/5
      </span>
      <span className="text-sm text-gray-500 font-body">
        ({count} {count === 1 ? "review" : "reviews"})
      </span>
    </div>
  );
}

function BCScoreInput({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: 5 }, (_, i) => (
        <button
          key={`i-${i}`}
          type="button"
          onClick={() => onChange(i + 1)}
          onMouseEnter={() => setHover(i + 1)}
          onMouseLeave={() => setHover(0)}
          className="p-0.5"
        >
          <Star
            className={`h-6 w-6 transition-colors ${
              (hover || value) > i
                ? "fill-tbc-gold text-tbc-gold"
                : "text-gray-300"
            }`}
          />
        </button>
      ))}
      <span className="ml-2 text-lg font-heading text-tbc-navy">
        {value || hover || 0}/5
      </span>
    </div>
  );
}

// ── Hero card ────────────────────────────────────────────────────────────
function BarberHeroCard({ business }) {
  const heroPortrait = resolveAssetUrl(business.image_url);
  const cityLine = [business.city, business.state].filter(Boolean).join(", ");
  const total = Number(business.chairs_total || 0);
  const booked = Math.min(Number(business.chairs_booked || 0), total);
  const openChairs = Math.max(0, total - booked);
  const showChairs = total > 0;

  return (
    <div
      className="relative overflow-hidden rounded-3xl bg-tbc-navy shadow-xl ring-1 ring-tbc-navy/10"
      data-testid="barber-hero-card"
    >
      {/* Right-side portrait */}
      <div className="absolute inset-y-0 right-0 w-[60%] sm:w-[55%] lg:w-[52%] xl:w-[48%]" aria-hidden="true">
        <img
          src={heroPortrait || FALLBACK_BARBER_HERO}
          alt=""
          className="h-full w-full object-cover"
          style={{ objectPosition: "50% 40%" }}
        />
        {/* Left-edge navy fade to keep text legible */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-tbc-navy via-tbc-navy/60 to-transparent sm:w-32" />
        {/* Subtle top+bottom vignette for depth */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-tbc-navy/10 via-transparent to-tbc-navy/40" />
      </div>

      <div className="relative grid min-h-[460px] grid-cols-[1fr_auto] gap-3 p-5 sm:min-h-[520px] sm:p-8 lg:min-h-[600px] lg:p-10 xl:min-h-[640px]">
        <div className="flex max-w-[62%] flex-col justify-between sm:max-w-[55%] lg:max-w-[50%]">
          {/* Badges */}
          <div className="flex flex-wrap items-center gap-2">
            <span
              data-testid="barber-category-badge"
              className="inline-flex items-center gap-1 rounded-full bg-tbc-gold px-3 py-1 text-xs font-semibold text-tbc-navy sm:text-sm"
            >
              <Scissors className="h-3.5 w-3.5" />
              Barber
            </span>
            {business.is_verified && (
              <span
                data-testid="barber-verified-badge"
                className="inline-flex items-center gap-1 rounded-full bg-tbc-gold px-3 py-1 text-xs font-semibold text-tbc-navy sm:text-sm"
              >
                <BadgeCheck className="h-3.5 w-3.5" />
                Verified
              </span>
            )}
            {business.is_founding_member && business.founding_member_number ? (
              <Link
                to={`/founding-100#${String(business.founding_member_number).padStart(3, "0")}`}
                data-testid="barber-founding-badge"
                className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-tbc-gold to-amber-400 px-3 py-1 text-xs font-bold text-tbc-navy sm:text-sm hover:shadow-lg transition-all"
                title={`Founding Business #${business.founding_member_number} — View the Founding 100`}
              >
                <Crown className="h-3.5 w-3.5" />
                Founding Business #{String(business.founding_member_number).padStart(3, "0")}
              </Link>
            ) : business.is_founding_member ? (
              <span
                data-testid="barber-founding-badge"
                className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-tbc-gold to-amber-400 px-3 py-1 text-xs font-bold text-tbc-navy sm:text-sm"
              >
                <Crown className="h-3.5 w-3.5" />
                Founding Business
              </span>
            ) : null}
          </div>

          {/* Name + city + tagline */}
          <div className="my-4 sm:my-6">
            <h1
              data-testid="barber-name"
              className="font-heading text-white leading-[1.02] tracking-tight"
              style={{ fontSize: "clamp(1.6rem, 6vw, 3.25rem)" }}
            >
              {business.name}
            </h1>
            {cityLine && (
              <p
                className="mt-3 font-heading text-white/95 leading-tight"
                style={{ fontSize: "clamp(1.05rem, 3.4vw, 1.85rem)" }}
                data-testid="barber-city-line"
              >
                {cityLine}
              </p>
            )}
            <p
              className="font-heading text-white/95 leading-tight"
              style={{ fontSize: "clamp(1.05rem, 3.4vw, 1.85rem)" }}
            >
              Master Barber
            </p>
            {business.tagline && (
              <p
                className="mt-3 font-heading italic text-tbc-gold leading-snug"
                style={{ fontSize: "clamp(0.85rem, 2.4vw, 1.25rem)" }}
                data-testid="barber-tagline"
              >
                {business.tagline}
              </p>
            )}
            <div className="mt-3 h-0.5 w-20 bg-tbc-gold/80 sm:w-24" />
          </div>

          {/* Chairs + book row */}
          <div
            className="grid grid-cols-2 gap-2 pt-2 sm:gap-3"
            data-testid="barber-chairs-row"
          >
            <div className="rounded-2xl bg-white/10 p-3 backdrop-blur-sm ring-1 ring-white/15">
              <div className="flex items-center gap-1.5 text-tbc-gold">
                <Users className="h-4 w-4" strokeWidth={1.7} />
                <span className="text-[11px] font-medium uppercase tracking-wider sm:text-xs">
                  Chairs
                </span>
              </div>
              {showChairs ? (
                <p
                  className="mt-1 font-heading text-white leading-tight"
                  style={{ fontSize: "clamp(1rem, 3vw, 1.35rem)" }}
                >
                  {openChairs}{" "}
                  <span className="text-white/60 font-body text-xs sm:text-sm">
                    open · {booked}/{total} booked
                  </span>
                </p>
              ) : (
                <p
                  className="mt-1 font-heading italic text-white/70 leading-tight"
                  style={{ fontSize: "clamp(0.85rem, 2.4vw, 1rem)" }}
                >
                  Walk-ins welcome
                </p>
              )}
            </div>
            <div className="rounded-2xl bg-white/10 p-3 backdrop-blur-sm ring-1 ring-white/15">
              <div className="flex items-center gap-1.5 text-tbc-gold">
                <CalendarClock className="h-4 w-4" strokeWidth={1.7} />
                <span className="text-[11px] font-medium uppercase tracking-wider sm:text-xs">
                  Booking
                </span>
              </div>
              {business.book_url ? (
                <a
                  href={business.book_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackEvent(business.business_id, "book_click", {
                      metadata: { channel: "barber_hero" },
                    })
                  }
                  data-testid="barber-book-link"
                  className="mt-1 inline-flex items-center gap-1 font-heading text-white hover:text-tbc-gold"
                  style={{ fontSize: "clamp(0.9rem, 2.6vw, 1.15rem)" }}
                >
                  Book online
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              ) : business.phone ? (
                <a
                  href={`tel:${business.phone}`}
                  onClick={() =>
                    trackEvent(business.business_id, "contact_click", {
                      metadata: { channel: "phone_hero" },
                    })
                  }
                  data-testid="barber-call-link"
                  className="mt-1 inline-flex items-center gap-1 font-heading text-white hover:text-tbc-gold"
                  style={{ fontSize: "clamp(0.9rem, 2.6vw, 1.15rem)" }}
                >
                  Call to book
                </a>
              ) : (
                <p
                  className="mt-1 font-heading italic text-white/70 leading-tight"
                  style={{ fontSize: "clamp(0.85rem, 2.4vw, 1rem)" }}
                >
                  Contact for hours
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="w-[38%] sm:w-[45%] lg:w-[48%] xl:w-[45%]" aria-hidden="true" />
      </div>
    </div>
  );
}

// ── Admin action buttons row ─────────────────────────────────────────────
function BarberActionButtons({
  business,
  isAdmin,
  onEdit,
  onAssignOwner,
  onClaimQR,
  onDelete,
  onToggleGoogle,
}) {
  if (!isAdmin) return null;
  const disabled = !!business.google_reviews_disabled;
  return (
    <div
      className="grid grid-cols-4 gap-2 sm:gap-3"
      data-testid="barber-action-buttons"
    >
      <button
        type="button"
        onClick={onEdit}
        data-testid="barber-edit-btn"
        className="flex flex-col items-center justify-center gap-1.5 rounded-2xl border border-gray-200 bg-white px-2 py-3 text-tbc-navy shadow-sm transition hover:border-tbc-navy/30 hover:shadow-md sm:py-4"
      >
        <Pencil className="h-5 w-5" strokeWidth={1.8} />
        <span
          className="font-body font-medium leading-tight"
          style={{ fontSize: "clamp(0.7rem, 2.2vw, 0.95rem)" }}
        >
          Edit
        </span>
      </button>
      <button
        type="button"
        onClick={onAssignOwner}
        data-testid="barber-assign-owner-btn"
        className="flex flex-col items-center justify-center gap-1.5 rounded-2xl bg-tbc-gold px-2 py-3 text-tbc-navy shadow-sm transition hover:bg-tbc-gold/90 hover:shadow-md sm:py-4"
      >
        <UserCheck className="h-5 w-5" strokeWidth={1.8} />
        <span
          className="text-center font-body font-semibold leading-tight"
          style={{ fontSize: "clamp(0.7rem, 2.2vw, 0.95rem)" }}
        >
          Assign<br className="sm:hidden" /> owner
        </span>
      </button>
      {!business.is_claimed ? (
        <button
          type="button"
          onClick={onClaimQR}
          data-testid="barber-claim-qr-btn"
          className="flex flex-col items-center justify-center gap-1.5 rounded-2xl border border-gray-200 bg-white px-2 py-3 text-tbc-navy shadow-sm transition hover:border-tbc-navy/30 hover:shadow-md sm:py-4"
        >
          <QrCode className="h-5 w-5" strokeWidth={1.8} />
          <span
            className="text-center font-body font-medium leading-tight"
            style={{ fontSize: "clamp(0.7rem, 2.2vw, 0.95rem)" }}
          >
            Claim<br className="sm:hidden" /> QR
          </span>
        </button>
      ) : (
        <button
          type="button"
          onClick={onDelete}
          data-testid="barber-delete-btn"
          className="flex flex-col items-center justify-center gap-1.5 rounded-2xl border border-red-200 bg-white px-2 py-3 text-red-600 shadow-sm transition hover:border-red-300 hover:shadow-md sm:py-4"
        >
          <Trash2 className="h-5 w-5" strokeWidth={1.8} />
          <span
            className="text-center font-body font-medium leading-tight"
            style={{ fontSize: "clamp(0.7rem, 2.2vw, 0.95rem)" }}
          >
            Delete
          </span>
        </button>
      )}
      <button
        type="button"
        onClick={onToggleGoogle}
        data-testid="barber-toggle-google-btn"
        className={`flex flex-col items-center justify-center gap-1.5 rounded-2xl border bg-white px-2 py-3 text-tbc-navy shadow-sm transition hover:shadow-md sm:py-4 ${
          disabled ? "border-tbc-navy/25" : "border-red-200"
        }`}
      >
        <StarOff className="h-5 w-5" strokeWidth={1.8} />
        <span
          className="text-center font-body font-medium leading-tight"
          style={{ fontSize: "clamp(0.7rem, 2.2vw, 0.95rem)" }}
        >
          {disabled ? (
            <>
              Enable<br className="sm:hidden" /> Google<br className="sm:hidden" /> reviews
            </>
          ) : (
            <>
              Disable<br className="sm:hidden" /> Google<br className="sm:hidden" /> reviews
            </>
          )}
        </span>
      </button>
    </div>
  );
}

// ── Price sheet card ─────────────────────────────────────────────────────
function PriceSheetCard({ business }) {
  const rows =
    (Array.isArray(business.price_sheet) && business.price_sheet.length > 0
      ? business.price_sheet
      : DEFAULT_PRICE_SHEET
    ).slice(0, 12);
  const isDefault =
    !Array.isArray(business.price_sheet) || business.price_sheet.length === 0;

  return (
    <div
      className="rounded-2xl bg-tbc-cream/70 p-5 sm:p-6"
      data-testid="barber-price-sheet"
    >
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <h3 className="font-heading text-xl text-tbc-navy">Price Sheet</h3>
        <Sparkles className="h-4 w-4 text-tbc-gold" />
      </div>
      <ul className="divide-y divide-tbc-navy/10">
        {rows.map((row, idx) => (
          <li
            key={`p-${idx}`}
            className="flex items-baseline justify-between gap-3 py-2"
          >
            <span className="font-body text-sm text-tbc-navy sm:text-base">
              {row.service || row.name}
            </span>
            <span className="font-heading text-sm text-tbc-navy sm:text-base">
              {row.price}
            </span>
          </li>
        ))}
      </ul>
      {isDefault && (
        <p className="mt-3 text-xs italic text-gray-500">
          Sample prices — the shop hasn&apos;t published their menu yet.
        </p>
      )}
      {business.book_url && (
        <a
          href={business.book_url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() =>
            trackEvent(business.business_id, "book_click", {
              metadata: { channel: "price_sheet" },
            })
          }
          data-testid="barber-price-sheet-book"
          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-tbc-navy px-5 py-2.5 font-body font-semibold text-white hover:bg-tbc-navy/90"
        >
          <CalendarClock className="h-4 w-4" />
          Book a chair
        </a>
      )}
    </div>
  );
}

// ── Cuts gallery ─────────────────────────────────────────────────────────
function CutsGallery({ business }) {
  const imgs = Array.isArray(business.gallery_images)
    ? business.gallery_images.filter(Boolean).slice(0, 6)
    : [];
  if (imgs.length === 0) {
    return (
      <div
        className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center"
        data-testid="barber-gallery-empty"
      >
        <Camera className="h-6 w-6 text-gray-400" />
        <p className="font-body text-sm text-gray-500">
          The shop hasn&apos;t uploaded their cuts gallery yet.
        </p>
      </div>
    );
  }
  return (
    <div>
      <h3 className="mb-3 font-heading text-2xl text-tbc-navy">Recent Cuts</h3>
      <div
        className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3"
        data-testid="barber-cuts-gallery"
      >
        {imgs.map((src, idx) => (
          <div
            key={`cut-${idx}`}
            className="aspect-square overflow-hidden rounded-xl bg-gray-100"
          >
            <img
              src={resolveAssetUrl(src)}
              alt={`Cut ${idx + 1}`}
              className="h-full w-full object-cover transition duration-500 hover:scale-105"
              loading="lazy"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── BC Score card ────────────────────────────────────────────────────────
function BarberBCScoreCard({ business, user, onOpenReview }) {
  const isOwner = user && user.user_id === business.owner_id;
  return (
    <div
      className="rounded-2xl bg-tbc-cream/70 p-5 sm:p-6"
      data-testid="barber-bc-score-card"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-heading text-lg text-tbc-navy sm:text-xl">BC Score</h3>
          <div className="mt-2">
            <BCScoreDisplay
              score={business.bc_score || 0}
              count={business.bc_score_count || 0}
            />
          </div>
          {isOwner && (
            <p className="mt-3 text-sm italic text-gray-600">
              You can&apos;t review your own business.
            </p>
          )}
        </div>
        {!isOwner && (
          <Button
            onClick={onOpenReview}
            className="rounded-full bg-tbc-gold px-5 py-2.5 font-body font-semibold text-tbc-navy hover:bg-tbc-gold/90"
            data-testid="barber-rate-btn"
          >
            <Star className="mr-2 h-4 w-4" />
            Rate This Shop
          </Button>
        )}
      </div>
    </div>
  );
}

// ── Contact + hours ──────────────────────────────────────────────────────
function BarberContactBlock({ business }) {
  const hasContact =
    business.address ||
    business.city ||
    business.phone ||
    business.email ||
    business.website;
  const hasHours = business.hours && Object.keys(business.hours).length > 0;
  if (!hasContact && !hasHours) return null;
  return (
    <div className="space-y-2 font-body text-[15px] text-gray-700">
      {(business.address || business.city) && (
        <div className="flex items-start gap-3">
          <MapPin className="mt-0.5 h-5 w-5 flex-none text-tbc-gold" />
          <div>
            {business.address && <p>{business.address}</p>}
            {(business.city || business.state) && (
              <p>
                {[business.city, business.state].filter(Boolean).join(", ")}
                {business.zip_code ? ` ${business.zip_code}` : ""}
              </p>
            )}
          </div>
        </div>
      )}
      {business.phone && (
        <div className="flex items-center gap-3">
          <Phone className="h-5 w-5 flex-none text-tbc-gold" />
          <a
            href={`tel:${business.phone}`}
            onClick={() =>
              trackEvent(business.business_id, "contact_click", {
                metadata: { channel: "phone" },
              })
            }
            className="hover:text-tbc-navy"
          >
            {business.phone}
          </a>
        </div>
      )}
      {business.email && (
        <div className="flex items-center gap-3">
          <Mail className="h-5 w-5 flex-none text-tbc-gold" />
          <a
            href={`mailto:${business.email}`}
            className="break-all hover:text-tbc-navy"
          >
            {business.email}
          </a>
        </div>
      )}
      {business.website && (
        <div className="flex items-center gap-3">
          <Globe className="h-5 w-5 flex-none text-tbc-gold" />
          <a
            href={business.website}
            target="_blank"
            rel="noopener noreferrer"
            className="break-all hover:text-tbc-navy"
          >
            {business.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
          </a>
        </div>
      )}
      {hasHours && (
        <div className="flex items-start gap-3 pt-1">
          <Clock className="mt-0.5 h-5 w-5 flex-none text-tbc-gold" />
          <div className="space-y-0.5">
            {Object.entries(business.hours).map(([day, hours]) => (
              <p key={day}>
                <span className="capitalize text-gray-500">{day}: </span>
                <span className="text-tbc-navy">{hours}</span>
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main layout ──────────────────────────────────────────────────────────
export default function BarberProfileLayout({
  business,
  user,
  isAdmin,
  isFavorited,
  setIsFavorited,
  onEdit,
  onAssignOwner,
  onClaimQR,
  onDelete,
  onToggleGoogleReviews,
  reviewsRefreshKey,
  onReviewSubmitted,
}) {
  const [reviewOpen, setReviewOpen] = useState(false);
  const [newReview, setNewReview] = useState({ score: 0, comment: "" });
  const [submitting, setSubmitting] = useState(false);

  const submitReview = async () => {
    if (newReview.score < 1) {
      toast.error("Please select a BC Score");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/businesses/${business.business_id}/reviews`,
        newReview,
        { withCredentials: true },
      );
      toast.success("Review submitted!");
      setReviewOpen(false);
      setNewReview({ score: 0, comment: "" });
      onReviewSubmitted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 pb-16 pt-6 sm:px-6 lg:px-10">
      <div className="flex items-center justify-between gap-3">
        <Link
          to="/directory"
          className="inline-flex items-center gap-2 font-body text-gray-700 hover:text-tbc-navy"
          data-testid="barber-back-to-directory"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Directory
        </Link>
        <div className="flex items-center gap-2">
          <FavoriteButton
            businessId={business.business_id}
            initial={isFavorited}
            size="md"
            onChange={setIsFavorited}
          />
          <ShareButton
            title={business.name}
            text={`Check out ${business.name} on The Blk Community`}
            variant="icon"
            testId="barber-share-btn"
            onShare={() => trackEvent(business.business_id, "share_click")}
          />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-6 lg:mt-6 lg:grid-cols-12 lg:gap-8">
        <div className="space-y-4 lg:col-span-8 lg:space-y-5">
          <BarberHeroCard business={business} />
          <BarberActionButtons
            business={business}
            isAdmin={isAdmin}
            onEdit={onEdit}
            onAssignOwner={onAssignOwner}
            onClaimQR={onClaimQR}
            onDelete={onDelete}
            onToggleGoogle={onToggleGoogleReviews}
          />

          {/* Cuts gallery lives under the hero on desktop so it feels
              like the shop's portfolio right next to the storefront. */}
          <div className="pt-2">
            <CutsGallery business={business} />
          </div>
        </div>

        <div className="space-y-6 lg:col-span-4">
          <PriceSheetCard business={business} />
          <BarberBCScoreCard
            business={business}
            user={user}
            onOpenReview={() => setReviewOpen(true)}
          />
          {business.description && (
            <section data-testid="barber-about-section">
              <h2 className="font-heading text-2xl text-tbc-navy sm:text-3xl">About</h2>
              <p
                className="mt-3 whitespace-pre-wrap font-body leading-relaxed text-gray-700"
                data-testid="barber-description"
              >
                {business.description}
              </p>
            </section>
          )}
          <section data-testid="barber-contact-section">
            <BarberContactBlock business={business} />
          </section>
        </div>
      </div>

      <div className="mt-10 space-y-8">
        <ReviewsSection
          business={business}
          currentUser={user}
          refreshKey={reviewsRefreshKey}
          onWriteReview={
            user && user.user_id === business.owner_id
              ? undefined
              : () => setReviewOpen(true)
          }
        />
        <BusinessExternalReviewsSection businessId={business.business_id} />
      </div>

      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              Rate {business.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pt-4">
            <div>
              <label className="mb-3 block text-sm font-medium text-gray-700">
                Your BC Score
              </label>
              <BCScoreInput
                value={newReview.score}
                onChange={(score) => setNewReview({ ...newReview, score })}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Your Review (Optional)
              </label>
              <Textarea
                value={newReview.comment}
                onChange={(e) =>
                  setNewReview({ ...newReview, comment: e.target.value })
                }
                placeholder="How was your cut?"
                rows={4}
              />
            </div>
            <Button
              onClick={submitReview}
              disabled={submitting || newReview.score < 1}
              className="w-full rounded-full bg-tbc-navy py-6 text-white hover:bg-tbc-navy/90"
              data-testid="barber-submit-review-btn"
            >
              {submitting ? "Submitting..." : "Submit Review"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
